import { useState, useCallback, useRef, useEffect } from "react";

import Color from "color";

import useStyles from "../../../Hooks/useStyles.ts";

import { useFormEditorContext } from "../../../Contexts/FormEditorContext";

import { ResizableTextarea } from "../../ui/resizable-textarea.tsx";

import FormButton from "../FormButton.tsx";

import {
    X,
    ImagePlus,
    Plus,
    Settings,
    CloudUpload,
    LoaderCircle,
} from "lucide-react";

import { Input } from "../../ui/input.tsx";

import { getLetterForIndex } from "../../../Utils/string";

import { v4 as uuidv4 } from "uuid";

import { useMutation } from "@tanstack/react-query";

import { useApi } from "../../../Api/useApi.ts";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../../ui/dialog.tsx";

import clsx from "clsx";

import { useParams } from "react-router-dom";

type Choice = {
    id: string;
    label: string;
    value: string | null;
};

export default function PictureChoice() {
    const { getStyles } = useStyles();
    const {
        theme,
        editorField,
        setEditorField,
        setFields,
        device,
        changeFieldTitle,
    } = useFormEditorContext();

    const updateField = (fieldId, key, subkey, value) => {
        setFields((prevFields) => {
            const updatedFields = prevFields.map((field) => {
                if (field.id === fieldId) {
                    if (subkey) {
                        return {
                            ...field,
                            [key]: {
                                ...field[key],
                                [subkey]: value,
                            },
                        };
                    } else {
                        return {
                            ...field,
                            [key]: value,
                        };
                    }
                }
                return field;
            });
            return updatedFields;
        });

        setEditorField((prev) => {
            if (subkey) {
                return {
                    ...prev,
                    [key]: {
                        ...prev[key],
                        [subkey]: value,
                    },
                };
            } else {
                return {
                    ...prev,
                    [key]: value,
                };
            }
        });

        changeFieldTitle(fieldId, value);
    };

    return (
        <>
            <div className="text-left flex flex-col w-full max-w-[606px] ">
                <ResizableTextarea
                    placeholder="Add question title"
                    value={editorField?.title || ""}
                    onChange={(value) =>
                        updateField(editorField?.id, "title", null, value)
                    }
                    style={{
                        color: theme?.text_color,
                        fontSize: getStyles("h2", theme?.text_size, device)
                            .fontSize,
                        fontWeight: getStyles("h2", theme?.text_size, device)
                            .fontWeight,
                        fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                    }}
                />
                <ResizableTextarea
                    placeholder="Add question description (Optional)"
                    value={editorField?.properties?.description || ""}
                    onChange={(value) =>
                        updateField(
                            editorField?.id,
                            "properties",
                            "description",
                            value,
                        )
                    }
                    style={{
                        color: theme?.text_color,
                        fontSize: getStyles("h4", theme?.text_size, device)
                            .fontSize,
                        fontWeight: getStyles("h4", theme?.text_size, device)
                            .fontWeight,
                        fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                    }}
                    className="mt-2"
                />

                <Choice />

                <FormButton className="mt-3" />
            </div>
        </>
    );
}

function Choice() {
    const { theme, editorField, setEditorField, setFields, device, fields } =
        useFormEditorContext();
    const api = useApi();
    const { id } = useParams();
    const saveTimeoutRef = useRef<any>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [imageUploadOpen, setImageUploadOpen] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Function to add a new choice
    const addChoice = () => {
        const newOption = {
            id: uuidv4(),
            label: "",
            value: null,
        };
        setEditorField((prev) => ({
            ...prev,
            properties: {
                ...prev.properties,
                choices: [...(prev.properties.choices || []), newOption],
            },
        }));
    };

    // Function to remove a choice
    const removeChoice = (id: string) => {
        const updatedField = {
            ...editorField,
            properties: {
                ...editorField.properties,
                choices: (editorField.properties.choices || []).filter(
                    (choice) => choice.id !== id,
                ),
            },
        };
        setEditorField(updatedField);
        scheduleSave(updatedField);
    };

    // Function to update choice text
    const scheduleSave = (updatedField) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveChanges(updatedField);
        }, 500); // Debounce save for 500ms
    };

    const saveChanges = useCallback(
        (updatedEditorField) => {
            const newFields = [...fields];
            const index = newFields.findIndex(
                (f) => f?.id === updatedEditorField.id,
            );

            const cleanEditorField = {
                ...updatedEditorField,
                title: updatedEditorField?.title || "",
                properties: {
                    ...updatedEditorField.properties,
                    description:
                        updatedEditorField.properties?.description || "",
                },
            };

            newFields[index] = Object.fromEntries(
                Object.entries(cleanEditorField).filter(
                    ([key, value]) => value != null,
                ),
            );

            setFields(newFields);
        },
        [fields, setFields],
    );

    // Cleanup function to clear any pending timeouts
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    const handleOnChange = useCallback((value, id) => {
        setEditorField((prev) => {
            const updatedField = {
                ...prev,
                properties: {
                    ...prev.properties,
                    choices: (prev.properties.choices || []).map((choice) =>
                        choice.id === id ? { ...choice, label: value } : choice,
                    ),
                },
            };

            scheduleSave(updatedField);
            return updatedField;
        });

        // Maintain focus after state update
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    }, []);

    const openImageUpload = (choiceId: string) => {
        setSelectedChoice(choiceId);
        setImageUploadOpen(true);
    };

    const uploadMutation = useMutation({
        mutationFn: async (file: FormData) => {
            const response = await api.files.uploadFile(file, id as string);
            const value = response?.data?.url;

            setEditorField((prev) => {
                const updatedField = {
                    ...prev,
                    properties: {
                        ...prev.properties,
                        choices: (prev.properties.choices || []).map((choice) =>
                            choice.id === selectedChoice
                                ? { ...choice, value }
                                : choice,
                        ),
                    },
                };
                scheduleSave(updatedField);
                return updatedField;
            });
            setImageUploadOpen(false);
            setIsUploading(false);
            setSelectedChoice(null);
        },
        onError: (error) => {
            setIsUploading(false);
            setSelectedChoice(null);
            setImageUploadOpen(false);
            console.error("Could not upload image", error);
        },
    });
    const handleImageSelect = (file: File) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        uploadMutation.mutate(formData);
    };

    const color = new Color(theme?.answer_text_color);
    const maxHeight = device === "desktop" ? "max-h-[432px]" : "max-h-[300px]";

    return (
        <>
            <div
                className={clsx(
                    `grid  no-scrollbar ${maxHeight} overflow-y-auto`,
                    device === "desktop"
                        ? " grid-cols-[repeat(auto-fill,174px)] mt-6 gap-3"
                        : "grid-cols-2 mt-4 gap-2",
                )}
            >
                {editorField?.properties?.choices?.map((choice, index) => (
                    <div
                        key={choice.id}
                        className={`border rounded-lg p-2 pb-0 relative`}
                        style={{
                            borderRadius: theme?.rounded_corners || 4,
                            borderColor: color.hex() + "50",
                            borderWidth: "1px",
                            backgroundColor: color.hex() + "10", // Changing the opacity of the color
                        }}
                    >
                        <div className="relative flex items-center group justify-center rounded-[2px] ">
                            <button
                                className={clsx(
                                    `absolute opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white rounded-full -top-4 -right-4`,
                                    editorField?.properties?.choices?.length ===
                                        1 && "hidden",
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeChoice(choice.id);
                                }}
                            >
                                <X
                                    size={14}
                                    style={{
                                        color: theme?.text_color,
                                    }}
                                />
                            </button>
                            {choice.value ? (
                                <>
                                    <div className="relative h-[158px] w-[158px]">
                                        <img
                                            src={
                                                choice.value ||
                                                "/placeholder.svg"
                                            }
                                            alt={`Choice ${choice.label}`}
                                            className="object-contain h-full"
                                        />
                                    </div>
                                </>
                            ) : (
                                <button
                                    className="flex border border-dashed flex-col items-center justify-center size-[158px]"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openImageUpload(choice.id);
                                    }}
                                >
                                    <ImagePlus
                                        className="w-8 h-8 "
                                        style={{
                                            color: theme?.text_color,
                                        }}
                                    />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <div
                                className="flex items-center justify-center bg-[#FFFFFF] rounded-lg text-center w-6 h-6"
                                style={{
                                    borderRadius:
                                        Number(theme?.rounded_corners || 4) - 2,
                                    borderColor: color.hex() + "50",
                                    borderWidth: "1px",
                                    color: theme?.answer_text_color,
                                    backgroundColor: "#FFFFFF",
                                }}
                            >
                                <p
                                    style={{
                                        fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                                        fontWeight: theme?.is_bold
                                            ? "bold"
                                            : "normal",
                                    }}
                                >
                                    {getLetterForIndex(index)}
                                </p>
                            </div>

                            <Input
                                type="text"
                                value={choice.label}
                                onChange={(e) =>
                                    handleOnChange(e.target.value, choice.id)
                                }
                                placeholder="Enter choice text"
                                className="p-0 truncate bg-transparent border-transparent max-w-[128px] border-none focus:border-transparent focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 "
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                style={{
                                    color:
                                        theme?.answer_text_color ||
                                        theme?.text_color,
                                    resize: "none",
                                    fontWeight: theme?.is_bold
                                        ? "bold"
                                        : "normal",
                                    fontFamily: `${theme?.font_family?.family}, ${theme?.font_family?.category}`,
                                }}
                            />
                        </div>
                    </div>
                ))}
                {editorField?.properties?.choices.length < 26 && (
                    <div
                        className="flex items-center min-h-[206px] justify-center h-full p-4 border rounded-lg cursor-pointer"
                        onClick={addChoice}
                    >
                        <Plus
                            className="w-12 h-12 "
                            style={{
                                color: theme?.text_color,
                            }}
                        />
                    </div>
                )}
            </div>

            <Dialog
                open={imageUploadOpen}
                onOpenChange={(open) => {
                    setImageUploadOpen(open);
                    if (!open) {
                        setIsUploading(false);
                    }
                }}
            >
                <DialogContent
                    onInteractOutside={(e) => {
                        e.preventDefault();
                        if (isUploading) {
                            setIsUploading(false);
                        }
                    }}
                    className="sm:w-[840px] max-w-[840px] max-h-[600px] w-full p-0 gap-0 top-[50%]"
                >
                    <DialogHeader className="p-3 h-[61px] border-b  ">
                        <DialogTitle className="flex items-center gap-1">
                            <Settings size={13} />
                            <span className=" text-[14px]">Upload Image</span>
                        </DialogTitle>
                        <DialogDescription className=" text-[12px]">
                            Choose an image to display with the choice
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-4">
                        <div
                            className="flex flex-col items-center justify-center border border-purple-300 border-dashed rounded-md cursor-pointer h-[507px]"
                            onClick={() =>
                                document.getElementById("file-upload")?.click()
                            }
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (
                                    e.dataTransfer.files &&
                                    e.dataTransfer.files[0]
                                ) {
                                    const file = e.dataTransfer.files[0];
                                    handleImageSelect(file);
                                }
                            }}
                        >
                            <input
                                id="file-upload"
                                type="file"
                                className="hidden"
                                disabled={isUploading}
                                accept="image/jpeg,image/png,image/bmp,image/webp,image/gif"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        // Create a URL for the selected file

                                        const file = e.target.files[0];
                                        handleImageSelect(file);
                                    }
                                }}
                            />
                            {isUploading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
                                    <LoaderCircle
                                        className="mr-1 text-gray-500 animate-spin"
                                        size={20}
                                    />
                                    <p className="text-sm text-gray-500">
                                        Uploading...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <CloudUpload
                                            size={24}
                                            className=" text-primary"
                                        />
                                    </div>
                                    <p className="mb-1 text-base font-medium text-center">
                                        Choose file or drag and drop here
                                    </p>
                                    <p className="text-sm text-center text-[#626F86]">
                                        JPEG, PNG, BMP, WEBP, GIF up to 2MB
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
